/**
 * ActivationChecklist — the first-week "do these four things" card.
 * Stays on the dashboard until the member has answered their first 10 questions,
 * then disappears permanently.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { CheckCircle2, Circle, ChevronRight, Rocket } from "lucide-react";

const ActivationChecklist = ({ onOpenOnboarding, onVisibility }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get("/activation/checklist"));
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { onVisibility?.(Boolean(data?.show)); }, [data, onVisibility]);

  if (!data?.show) return null;

  const act = (item) => {
    if (item.done) return;
    if (item.action === "tour") window.dispatchEvent(new Event("passadc:start-tour"));
    else if (item.action === "onboarding") onOpenOnboarding?.();
    else if (item.to) navigate(item.to);
  };

  const pct = Math.round((data.done_count / data.total) * 100);

  return (
    <div data-testid="activation-checklist" className="glass rounded-2xl border border-primary/25 bg-primary/[0.04] p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
          <Rocket className="w-4 h-4 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold">Get set up for the exam</p>
          <p className="text-sm text-muted-foreground">
            Four things in your first week. This card goes away once you&apos;ve answered {data.target} questions.
          </p>
        </div>
        <span data-testid="checklist-progress" className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">
          {data.done_count} of {data.total} done
        </span>
      </div>

      <div className="h-1.5 rounded-full bg-secondary overflow-hidden mb-4">
        <div className="h-full rounded-full bg-primary transition-all duration-700" style={{ width: `${Math.max(pct, 2)}%` }} />
      </div>

      <ul className="space-y-1">
        {data.items.map((item) => (
          <li key={item.id}>
            <button
              onClick={() => act(item)}
              disabled={item.done}
              data-testid={`checklist-item-${item.id}`}
              className={`w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-colors ${
                item.done ? "opacity-60 cursor-default" : "hover:bg-secondary/70"
              }`}
            >
              {item.done
                ? <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0" />
                : <Circle className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
              <span className="min-w-0 flex-1">
                <span className={`block text-sm font-medium ${item.done ? "line-through" : ""}`}>
                  {item.label}
                  {item.progress && !item.done && (
                    <span className="ml-2 text-xs text-primary tabular-nums">
                      {item.progress.current}/{item.progress.target}
                    </span>
                  )}
                </span>
                {!item.done && <span className="block text-xs text-muted-foreground">{item.hint}</span>}
              </span>
              {!item.done && <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ActivationChecklist;
